/**
 * Image Gallery with Lightbox
 * Responsive gallery with fullscreen lightbox and slider functionality
 */

class ImageGallery {
  constructor(container) {
    this.container = container;
    this.items = Array.from(container.querySelectorAll('.image-gallery__item'));
    this.currentIndex = 0;
    this.lightbox = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isAnimating = false;
    
    this.init();
  }

  init() {
    // Create lightbox
    this.createLightbox();
    
    // Bind events
    this.items.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.openLightbox(index);
      });
      
      // Keyboard accessibility
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openLightbox(index);
        }
      });
    });
  }

  createLightbox() {
    // Create lightbox HTML
    const lightboxHTML = `
      <div class="gallery-lightbox" role="dialog" aria-modal="true" aria-label="Image gallery lightbox">
        <div class="gallery-lightbox__container">
          <button class="gallery-lightbox__close" aria-label="Close lightbox" title="Close (Esc)">
            <svg viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          
          <button class="gallery-lightbox__nav gallery-lightbox__nav--prev" aria-label="Previous image" title="Previous (←)">
            <svg viewBox="0 0 24 24">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          
          <button class="gallery-lightbox__nav gallery-lightbox__nav--next" aria-label="Next image" title="Next (→)">
            <svg viewBox="0 0 24 24">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
          
          <div class="gallery-lightbox__image-wrapper">
            <div class="gallery-lightbox__loader"></div>
            <img class="gallery-lightbox__image" src="" alt="" />
          </div>
          
          <div class="gallery-lightbox__caption hide"></div>
          <div class="gallery-lightbox__counter hide"></div>
          <div class="gallery-lightbox__thumbnails hide"></div>
        </div>
      </div>
    `;
    
    // Insert into DOM
    document.body.insertAdjacentHTML('beforeend', lightboxHTML);
    
    // Get references
    this.lightbox = document.querySelector('.gallery-lightbox');
    this.lightboxImage = this.lightbox.querySelector('.gallery-lightbox__image');
    this.lightboxLoader = this.lightbox.querySelector('.gallery-lightbox__loader');
    this.lightboxCaption = this.lightbox.querySelector('.gallery-lightbox__caption');
    this.lightboxCounter = this.lightbox.querySelector('.gallery-lightbox__counter');
    this.lightboxThumbs = this.lightbox.querySelector('.gallery-lightbox__thumbnails');
    this.closeBtn = this.lightbox.querySelector('.gallery-lightbox__close');
    this.prevBtn = this.lightbox.querySelector('.gallery-lightbox__nav--prev');
    this.nextBtn = this.lightbox.querySelector('.gallery-lightbox__nav--next');
    
    // Bind lightbox events
    this.bindLightboxEvents();
  }

  bindLightboxEvents() {
    // Close button
    this.closeBtn.addEventListener('click', () => this.closeLightbox());
    
    // Navigation
    this.prevBtn.addEventListener('click', () => this.prevImage());
    this.nextBtn.addEventListener('click', () => this.nextImage());
    
    // Click outside to close
    this.lightbox.addEventListener('click', (e) => {
      if (e.target === this.lightbox || e.target === this.lightbox.querySelector('.gallery-lightbox__container')) {
        this.closeLightbox();
      }
    });
    
    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.lightbox.classList.contains('active')) return;
      
      switch(e.key) {
        case 'Escape':
          this.closeLightbox();
          break;
        case 'ArrowLeft':
          this.prevImage();
          break;
        case 'ArrowRight':
          this.nextImage();
          break;
      }
    });
    
    // Touch events for swipe
    this.lightbox.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.lightbox.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
    
    // Mouse wheel navigation (optional)
    this.lightbox.addEventListener('wheel', (e) => {
      if (this.isAnimating) return;
      
      if (e.deltaY > 0) {
        this.nextImage();
      } else if (e.deltaY < 0) {
        this.prevImage();
      }
    }, { passive: true });
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swiped left - next image
        this.nextImage();
      } else {
        // Swiped right - previous image
        this.prevImage();
      }
    }
  }

  openLightbox(index) {
    this.currentIndex = index;
    
    // Prevent body scroll
    document.body.classList.add('gallery-lightbox-open');
    
    // Show lightbox
    this.lightbox.classList.add('active');
    
    // Load image
    this.loadImage(index);
    
    // Update controls
    this.updateControls();
    
    // Create thumbnails if enabled
    this.createThumbnails();
    
    // Focus management
    this.closeBtn.focus();
  }

  closeLightbox() {
    this.lightbox.classList.remove('active');
    document.body.classList.remove('gallery-lightbox-open');
    
    // Return focus to clicked item
    if (this.items[this.currentIndex]) {
      this.items[this.currentIndex].focus();
    }
  }

  loadImage(index) {
    if (this.isAnimating) return;
    this.isAnimating = true;
    
    const item = this.items[index];
    if (!item) return;
    
    const img = item.querySelector('img');
    const caption = item.dataset.caption || img.alt || '';
    
    // Show loader
    this.lightboxLoader.style.display = 'block';
    this.lightboxImage.classList.remove('loaded');
    
    // Get high-res image URL
    const fullImageUrl = item.dataset.fullImage || img.src;
    
    // Preload image
    const tempImg = new Image();
    tempImg.onload = () => {
      this.lightboxImage.src = fullImageUrl;
      this.lightboxImage.alt = caption;
      this.lightboxLoader.style.display = 'none';
      
      // Trigger animation
      requestAnimationFrame(() => {
        this.lightboxImage.classList.add('loaded');
        this.isAnimating = false;
      });
      
      // Update caption
      if (caption) {
        this.lightboxCaption.textContent = caption;
        this.lightboxCaption.classList.remove('hide');
      } else {
        this.lightboxCaption.classList.add('hide');
      }
      
      // Preload adjacent images
      this.preloadAdjacentImages(index);
    };
    
    tempImg.onerror = () => {
      this.lightboxLoader.style.display = 'none';
      this.isAnimating = false;
      console.error('Failed to load image:', fullImageUrl);
    };
    
    tempImg.src = fullImageUrl;
  }

  preloadAdjacentImages(index) {
    // Preload previous and next images for smooth navigation
    const prevIndex = index - 1;
    const nextIndex = index + 1;
    
    [prevIndex, nextIndex].forEach(i => {
      if (i >= 0 && i < this.items.length) {
        const item = this.items[i];
        const img = item.querySelector('img');
        const fullImageUrl = item.dataset.fullImage || img.src;
        
        const preloadImg = new Image();
        preloadImg.src = fullImageUrl;
      }
    });
  }

  prevImage() {
    if (this.isAnimating) return;
    
    this.currentIndex--;
    if (this.currentIndex < 0) {
      this.currentIndex = this.items.length - 1;
    }
    
    this.loadImage(this.currentIndex);
    this.updateControls();
  }

  nextImage() {
    if (this.isAnimating) return;
    
    this.currentIndex++;
    if (this.currentIndex >= this.items.length) {
      this.currentIndex = 0;
    }
    
    this.loadImage(this.currentIndex);
    this.updateControls();
  }

  updateControls() {
    // Update counter
    const showCounter = this.container.dataset.showCounter !== 'false';
    if (showCounter) {
      this.lightboxCounter.textContent = `${this.currentIndex + 1} / ${this.items.length}`;
      this.lightboxCounter.classList.remove('hide');
    } else {
      this.lightboxCounter.classList.add('hide');
    }
    
    // Update navigation buttons
    const showArrows = this.container.dataset.showArrows !== 'false';
    if (showArrows) {
      this.prevBtn.classList.remove('hide');
      this.nextBtn.classList.remove('hide');
      
      // Disable buttons at boundaries if not looping
      const loop = this.container.dataset.loop !== 'false';
      if (!loop) {
        this.prevBtn.disabled = this.currentIndex === 0;
        this.nextBtn.disabled = this.currentIndex === this.items.length - 1;
      } else {
        this.prevBtn.disabled = false;
        this.nextBtn.disabled = false;
      }
    } else {
      this.prevBtn.classList.add('hide');
      this.nextBtn.classList.add('hide');
    }
    
    // Update active thumbnail
    this.updateActiveThumbnail();
  }

  createThumbnails() {
    const showThumbs = this.container.dataset.showThumbnails === 'true';
    
    if (!showThumbs || this.items.length <= 1) {
      this.lightboxThumbs.classList.add('hide');
      return;
    }
    
    // Clear existing thumbnails
    this.lightboxThumbs.innerHTML = '';
    
    // Create thumbnail for each image
    this.items.forEach((item, index) => {
      const img = item.querySelector('img');
      const thumb = document.createElement('div');
      thumb.className = 'gallery-lightbox__thumb';
      if (index === this.currentIndex) {
        thumb.classList.add('active');
      }
      
      thumb.innerHTML = `<img src="${img.src}" alt="" />`;
      thumb.addEventListener('click', () => {
        this.currentIndex = index;
        this.loadImage(index);
        this.updateControls();
      });
      
      this.lightboxThumbs.appendChild(thumb);
    });
    
    this.lightboxThumbs.classList.remove('hide');
  }

  updateActiveThumbnail() {
    const thumbs = this.lightboxThumbs.querySelectorAll('.gallery-lightbox__thumb');
    thumbs.forEach((thumb, index) => {
      if (index === this.currentIndex) {
        thumb.classList.add('active');
        // Scroll thumbnail into view
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      } else {
        thumb.classList.remove('active');
      }
    });
  }
}

// Initialize all galleries on page
document.addEventListener('DOMContentLoaded', () => {
  const galleries = document.querySelectorAll('.image-gallery__grid');
  galleries.forEach(gallery => {
    new ImageGallery(gallery);
  });
});

// Re-initialize on Shopify section reload (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const gallery = event.target.querySelector('.image-gallery__grid');
    if (gallery) {
      new ImageGallery(gallery);
    }
  });
}
